import { useTranslation } from 'react-i18next'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { ListItemCard } from 'src/components/layout/item-collection'
import { accountTypeLabelMap, PaymentAccount } from 'src/services/payment-accounts/models'
import type { ViewMode } from 'src/hooks/use-view-mode'

interface PaymentAccountItemProps {
  account: PaymentAccount
  viewMode: ViewMode
  onEdit: (account: PaymentAccount) => void
  onDelete: (account: PaymentAccount) => void
}

/** Table header for the list view; hidden below the `@3xl` container width. */
export const PaymentAccountListHeader = () => {
  const { t } = useTranslation()
  return (
    <div className="hidden rounded-lg bg-surface-hover px-6 py-3 text-sm font-medium text-text-secondary @3xl:flex">
      <div className="flex-1">{t('paymentAccountsPage.tableHeaderName')}</div>
      <div className="@3xl:w-40 @3xl:shrink-0 @3xl:text-center">
        {t('paymentAccountsPage.tableHeaderInstitution')}
      </div>
      <div className="@3xl:w-32 @3xl:shrink-0 @3xl:text-center">
        {t('paymentAccountsPage.tableHeaderType')}
      </div>
      <div className="@3xl:w-28 @3xl:shrink-0 @3xl:text-center">
        {t('paymentAccountsPage.tableHeaderLastFour')}
      </div>
      <div className="w-20" />
    </div>
  )
}

export const PaymentAccountItem = ({
  account,
  viewMode,
  onEdit,
  onDelete,
}: PaymentAccountItemProps) => {
  const { t } = useTranslation()
  const institution = account.institution || '-'
  const typeLabel = account.accountType ? accountTypeLabelMap[account.accountType] : '-'
  const lastFour = account.accountLastFour ? `••••${account.accountLastFour}` : '-'
  const ownerName = account.organizationRef?.name || null
  const testIdSuffix = viewMode === 'card' ? '-card' : ''

  const actions = (
    <>
      <Button
        variant="ghost"
        tone="neutral"
        iconOnly
        size="sm"
        shape="square"
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onEdit(account)
        }}
        tooltip={t('paymentAccountsPage.editAccount')}
        data-testid={`payment-account-edit${testIdSuffix}-${account.id}`}
      >
        <PencilIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        tone="error"
        iconOnly
        size="sm"
        shape="square"
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDelete(account)
        }}
        tooltip={t('paymentAccountsPage.deleteAccount')}
        data-testid={`payment-account-delete${testIdSuffix}-${account.id}`}
      >
        <TrashIcon className="h-4 w-4" />
      </Button>
    </>
  )

  if (viewMode === 'card') {
    return (
      <ListItemCard data-testid={`payment-account-card-${account.id}`}>
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-text-primary" title={account.name}>
              {account.name}
            </h3>
            {ownerName && (
              <p className="truncate text-sm text-text-placeholder" title={ownerName}>
                {ownerName}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">{actions}</div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">
              {t('paymentAccountsPage.tableHeaderInstitution')}
            </dt>
            <dd className="truncate font-medium text-text-primary" title={institution}>
              {institution}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">
              {t('paymentAccountsPage.tableHeaderType')}
            </dt>
            <dd className="truncate font-medium text-text-primary" title={typeLabel}>
              {typeLabel}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">
              {t('paymentAccountsPage.tableHeaderLastFour')}
            </dt>
            <dd className="truncate font-medium text-text-primary">{lastFour}</dd>
          </div>
        </dl>
      </ListItemCard>
    )
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-border bg-surface px-6 py-4 shadow transition-shadow hover:shadow-md @3xl:flex-row @3xl:items-center"
      data-testid={`payment-account-row-${account.id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="break-words font-medium text-text-primary">{account.name}</p>
      </div>
      <div className="text-sm text-text-secondary @3xl:w-40 @3xl:shrink-0 @3xl:text-center">
        <span className="font-medium text-text-placeholder @3xl:hidden">
          {t('paymentAccountsPage.tableHeaderInstitution')}:{' '}
        </span>
        {institution}
      </div>
      <div className="text-sm text-text-secondary @3xl:w-32 @3xl:shrink-0 @3xl:text-center">
        <span className="font-medium text-text-placeholder @3xl:hidden">
          {t('paymentAccountsPage.tableHeaderType')}:{' '}
        </span>
        {typeLabel}
      </div>
      <div className="text-sm text-text-secondary @3xl:w-28 @3xl:shrink-0 @3xl:text-center">
        <span className="font-medium text-text-placeholder @3xl:hidden">
          {t('paymentAccountsPage.tableHeaderLastFour')}:{' '}
        </span>
        {lastFour}
      </div>
      <div className="flex items-center justify-end gap-1 @3xl:w-20 @3xl:shrink-0">{actions}</div>
    </div>
  )
}
